import { __assign } from "tslib";
import { createBrowserHistory as baseCreateBrowserHistory, createMemoryHistory as baseCreateMemoryHistory, } from 'history';
import { act, observe } from 'retil-source';
import { createActionMap, parseLocation, resolveAction } from './historyUtils';
var defaultLocationReducer = function (location, action) { return resolveAction(action, location.pathname); };
export function createBrowserHistory(options) {
    return createHistoryService(baseCreateBrowserHistory(options));
}
export function createMemoryHistory(initialLocation) {
    return createHistoryService(baseCreateMemoryHistory({
        initialEntries: [parseLocation(initialLocation)],
    }));
}
export function createHistoryService(history, locationReducer) {
    if (locationReducer === void 0) { locationReducer = defaultLocationReducer; }
    var forceChange = false;
    var lastRequest = __assign(__assign({}, parseLocation(history.location)), { key: history.location.key });
    var plannedRequests = createActionMap();
    var source = observe(function (next) {
        next(lastRequest);
        return history.listen(function (_a) {
            var location = _a.location;
            var parsedLocation = parseLocation(location);
            var plannedRequest = plannedRequests.get(parsedLocation);
            lastRequest = __assign(__assign({}, (plannedRequest || parsedLocation)), { key: location.key });
            plannedRequests.clear();
            next(lastRequest);
        });
    });
    var runMaybeBlockedAction = function (callback) {
        var key = history.location.key;
        return new Promise(function (resolve) {
            return act(source, callback).finally(function () {
                resolve(history.location.key !== key);
            });
        });
    };
    var controller = {
        back: function () {
            return runMaybeBlockedAction(function () {
                history.back();
            });
        },
        block: function (predicate) {
            var unblock = history.block(function (tx) {
                if (forceChange) {
                    unblock();
                    tx.retry();
                }
                else {
                    var location_1 = parseLocation(tx.location);
                    act(source, function () {
                        return predicate(location_1, tx.action).then(function (shouldBlock) {
                            if (!shouldBlock) {
                                unblock();
                                tx.retry();
                            }
                        });
                    });
                }
            });
            return unblock;
        },
        navigate: function (action, options) {
            var _a = (options || {}).replace, replace = _a === void 0 ? false : _a;
            var location;
            return runMaybeBlockedAction(function () {
                location = locationReducer(lastRequest, action);
                forceChange = !!(options === null || options === void 0 ? void 0 : options.force);
                try {
                    history[replace ? 'replace' : 'push'](location, location.state);
                }
                finally {
                    forceChange = false;
                }
            });
        },
        plan: function (action) {
            var location = locationReducer(lastRequest, action);
            var plannedRequest = plannedRequests.get(location);
            if (!plannedRequest) {
                plannedRequest = __assign(__assign({}, location), { planId: Symbol() });
                delete plannedRequest.key;
                plannedRequests.set(location, plannedRequest);
            }
            return Promise.resolve(plannedRequest);
        },
    };
    return [source, controller];
}
